import React, { useState, useEffect, useMemo } from "react";
import {
  Badge,
  Button,
  Modal,
  ListGroup,
  OverlayTrigger,
  Tooltip,
  Spinner,
  Alert,
} from "react-bootstrap";
import { BellFill } from "react-bootstrap-icons";
import { useAuth } from "@clerk/clerk-react";
import createAxiosInstance from "../utility/utils";

const AbsenceNotification = () => {
  const { getToken } = useAuth();
  const axiosInstance = useMemo(
    () => createAxiosInstance(getToken),
    [getToken]
  );
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [error, setError] = useState(null);

  const fetchNotifications = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axiosInstance.get("/api/admin/absence-notifications");
      if (res.data && res.data.notifications) {
        setNotifications(res.data.notifications);
      } else {
        setNotifications([]);
      }
    } catch (err) {
      console.error("Error fetching notifications:", err);
      setError("Failed to fetch notifications");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const themeColor = "rgb(79, 81, 163)";

  return (
    <>
      <OverlayTrigger
        placement="bottom"
        overlay={
          <Tooltip>
            {notifications.length > 0
              ? `${notifications.length} student(s) with consecutive absences`
              : "No absence notifications"}
          </Tooltip>
        }
      >
        <Button
          variant="link"
          className="position-relative p-1"
          onClick={() => setShowModal(true)}
          style={{ color: themeColor }}
        >
          <BellFill size={20} />
          {notifications.length > 0 && (
            <Badge
              bg="danger"
              pill
              className="position-absolute top-0 start-100 translate-middle"
              style={{ fontSize: '0.65rem' }}
            >
              {notifications.length > 99 ? "99+" : notifications.length}
            </Badge>
          )}
        </Button>
      </OverlayTrigger>

      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
        <Modal.Header closeButton style={{ backgroundColor: themeColor, color: "white" }}>
          <Modal.Title>
            <BellFill className="me-2" /> 
            Absence Notifications
            {notifications.length > 0 && (
              <Badge bg="danger" pill className="ms-2">
                {notifications.length}
              </Badge>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loading ? (
            <div className="text-center py-4">
              <Spinner animation="border" style={{ color: themeColor }} />
              <p className="mt-2">Loading notifications...</p>
            </div>
          ) : error ? (
            <Alert variant="danger">
              <p>{error}</p>
              <Button variant="outline-primary" size="sm" onClick={fetchNotifications}>
                Try Again
              </Button>
            </Alert>
          ) : notifications.length > 0 ? (
            <ListGroup variant="flush">
              {notifications.map((notif, index) => (
                <ListGroup.Item key={index} className="py-3">
                  <div className="d-flex flex-column flex-md-row justify-content-between align-items-start">
                    <div>
                      <div className="mb-2">
                        <h6 className="mb-0">{notif.studentName}</h6>
                      </div>
                      <p className="mb-2">
                        <strong>Consecutive Absences: {notif.consecutiveDays} days</strong>
                      </p>
                      <div className="small text-muted">
                        <div>USN: {notif.studentUSN || "N/A"}</div>
                        <div>Email: {notif.studentEmail || "N/A"}</div>
                        <div>Last Absence: {formatDate(notif.lastAbsenceDate)}</div>
                      </div>
                    </div>
                    <div className="mt-3 mt-md-0">
                    <a 
  href={`https://mail.google.com/mail/?view=cm&fs=1&to=${notif.studentEmail}&su=Regarding%20Your%20Consecutive%20Absences&body=Dear%20${notif.studentName},%0A%0AWe%20noticed%20you%20have%20been%20absent%20for%20${notif.consecutiveDays}%20consecutive%20days.%0A%0APlease%20contact%20the%20admin%20office%20regarding%20your%20attendance.%0A%0ARegards,%0AAdmin`}
  className="btn btn-sm"
  style={{ backgroundColor: themeColor, color: "white" }}
  target="_blank"
  rel="noopener noreferrer"
>
  Contact Student
</a>

                    </div>
                  </div>
                </ListGroup.Item>
              ))}
            </ListGroup>
          ) : (
            <div className="text-center py-4">
              <p>No active absence notifications</p>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Close
          </Button>
          <Button 
            style={{ backgroundColor: themeColor, borderColor: themeColor }} 
            onClick={fetchNotifications}
          >
            Refresh
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default AbsenceNotification;